<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 d-inline-block mb-0">{{ $t("logs.title") }}</h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <card
        class="no-border-card"
        body-classes="px-0 pb-1"
        footer-classes="pb-2"
      >
        <template slot="header">
          <div class="row">
            <div class="col-lg-6">
              <h3 class="mb-0">{{ $t("logs.title") }}</h3>
            </div>
            <div class="col-lg-6 text-right">
              <base-button size="sm" type="primary" @click="search">
                {{ $t("logs.search") }}
              </base-button>
              <base-button
                v-if="isDtacAdmin"
                size="sm"
                type="neutral"
                @click="runExport"
                :disabled="exporting"
              >
                {{ $t("export.button") }}
              </base-button>
            </div>
          </div>
        </template>

        <div class="px-3 pb-3">
          <div class="form-group row">
            <div class="col-md-3">
              <base-input :label="$t('logs.range')">
                <el-select
                  v-model="relativeRange"
                  class="select-primary"
                  @change="onRelativeChange"
                >
                  <el-option
                    v-for="opt in relativeOptions"
                    :key="opt.value"
                    :value="opt.value"
                    :label="$t(opt.label)"
                  ></el-option>
                </el-select>
              </base-input>
            </div>
            <div class="col-md-3" v-if="relativeRange === 'custom'">
              <base-input :label="$t('logs.start_date')">
                <flat-picker
                  slot-scope="{ focus, blur }"
                  @on-open="focus"
                  @on-close="blur"
                  :config="datepickerConfig"
                  class="form-control datepicker"
                  v-model="start_date"
                >
                </flat-picker>
              </base-input>
            </div>
            <div class="col-md-3" v-if="relativeRange === 'custom'">
              <base-input :label="$t('logs.end_date')">
                <flat-picker
                  slot-scope="{ focus, blur }"
                  @on-open="focus"
                  @on-close="blur"
                  :config="datepickerConfig"
                  class="form-control datepicker"
                  v-model="end_date"
                >
                </flat-picker>
              </base-input>
            </div>
            <div :class="relativeRange === 'custom' ? 'col-md-3' : 'col-md-9'">
              <base-input
                v-model="session_id_filter"
                :label="$t('logs.session_id')"
                :placeholder="$t('logs.session_id_placeholder')"
              >
              </base-input>
            </div>
          </div>
        </div>

        <el-table
          :data="sessions"
          header-row-class-name="thead-light"
          v-loading="loading"
        >
          <el-table-column
            :label="$t('logs.session_id')"
            prop="SessionId"
            min-width="220"
          ></el-table-column>
          <el-table-column
            :label="$t('logs.user')"
            min-width="160"
          >
            <template slot-scope="{ row }">
              <span v-if="row.UserName">{{ row.UserName }}</span>
              <span v-else class="text-muted">—</span>
              <small v-if="row.UserId > 0" class="text-muted ml-1">
                #{{ row.UserId }}
              </small>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('logs.route')"
            prop="Route"
            min-width="220"
          ></el-table-column>
          <el-table-column
            :label="$t('logs.start_at')"
            prop="StartAt"
            min-width="170"
          ></el-table-column>
          <el-table-column
            :label="$t('logs.events')"
            prop="Events"
            align="right"
            min-width="80"
          ></el-table-column>
          <el-table-column
            :label="$t('logs.errors')"
            align="right"
            min-width="80"
          >
            <template slot-scope="{ row }">
              <span
                :class="row.Errors > 0 ? 'text-danger font-weight-bold' : ''"
              >{{ row.Errors }}</span>
            </template>
          </el-table-column>
          <el-table-column :label="$t('logs.actions')" min-width="120">
            <template slot-scope="{ row }">
              <base-button size="sm" type="primary" @click="openSession(row)">
                {{ $t("logs.view") }}
              </base-button>
            </template>
          </el-table-column>
        </el-table>

        <div
          slot="footer"
          class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
          v-if="total > 0"
        >
          <p class="card-category">
            {{ $t("data") }} {{ rangeFrom + 1 }} {{ $t("to") }} {{ rangeTo }}
            {{ $t("of") }} {{ total }}
          </p>
          <base-pagination
            class="pagination-no-border"
            :value="page"
            @input="onPageChange"
            :per-page="perPage"
            :total="total"
          >
          </base-pagination>
        </div>
      </card>
    </div>

    <modal :show.sync="exporting" :show-close="false" modal-classes="modal-sm">
      <div class="text-center py-3">
        <i class="fa fa-spinner fa-spin fa-2x text-primary mb-2"></i>
        <h4 class="mb-1">{{ $t("export.processing") }}</h4>
        <p class="text-muted mb-0">{{ $t("export.processing_text") }}</p>
      </div>
    </modal>

    <modal
      :show="exportError !== null"
      @close="clearExportError"
      modal-classes="modal-secondary"
    >
      <template slot="header">
        <h5 class="modal-title text-danger">
          <i class="fa fa-exclamation-triangle mr-2"></i>
          {{ exportError && exportError.title }}
        </h5>
      </template>
      <pre
        v-if="exportError && exportError.detail"
        class="export-error-detail"
      >{{ exportError.detail }}</pre>
      <template slot="footer">
        <button type="button" class="btn btn-link ml-auto" @click="clearExportError">
          {{ $t("close") }}
        </button>
      </template>
    </modal>

    <modal :show.sync="showModal" modal-classes="modal-lg">
      <template slot="header">
        <h5 class="modal-title">
          {{ $t("logs.timeline") }} —
          <small class="text-muted">{{ activeSessionId }}</small>
        </h5>
      </template>

      <div v-if="sessionEvents.length === 0" class="text-center text-muted py-4">
        {{ $t("logs.no_events") }}
      </div>

      <div class="timeline-wrapper" v-else>
        <div
          v-for="ev in sessionEvents"
          :key="ev.Id"
          class="timeline-item"
        >
          <div class="timeline-dot" :class="dotClass(ev.Level)"></div>
          <div class="timeline-content">
            <div class="d-flex justify-content-between flex-wrap align-items-center">
              <span class="font-weight-bold">{{ ev.FuncName }}</span>
              <div class="d-flex align-items-center">
                <small class="text-muted mr-2">{{ ev.EventAt }}</small>
                <button
                  type="button"
                  class="btn btn-sm btn-link p-1"
                  :title="$t('logs.copy_detail')"
                  @click="copyDetail(ev)"
                >
                  <i class="far fa-copy"></i>
                </button>
                <button
                  v-if="isSoap(ev)"
                  type="button"
                  class="btn btn-sm btn-link p-1"
                  :title="$t('logs.view_xml')"
                  @click="openXml(ev)"
                >
                  <i class="fas fa-code"></i>
                </button>
              </div>
            </div>
            <div>
              <span class="badge mr-2" :class="badgeClass(ev.Level)">{{ ev.Level }}</span>
              <small class="text-muted">{{ ev.Route }}</small>
              <small v-if="ev.UserName" class="text-muted ml-2">
                · {{ ev.UserName }}
              </small>
            </div>
            <pre class="timeline-detail">{{ ev.Detail }}</pre>
          </div>
        </div>
      </div>

      <template slot="footer">
        <button type="button" class="btn btn-link" @click="showModal = false">
          {{ $t("close") }}
        </button>
      </template>
    </modal>

    <modal :show.sync="showXmlModal" modal-classes="modal-lg">
      <template slot="header">
        <h5 class="modal-title">
          {{ $t("logs.xml_title") }}
          <small v-if="xmlFuncName" class="text-muted">— {{ xmlFuncName }}</small>
        </h5>
      </template>

      <pre class="xml-detail">{{ xmlPretty }}</pre>

      <template slot="footer">
        <button
          type="button"
          class="btn btn-secondary"
          @click="copyXml"
        >
          <i class="far fa-copy mr-1"></i>{{ $t("logs.copy_xml") }}
        </button>
        <button
          type="button"
          class="btn btn-link ml-auto"
          @click="showXmlModal = false"
        >
          {{ $t("close") }}
        </button>
      </template>
    </modal>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import Modal from "@/components/Modal";
import { BasePagination } from "@/components";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { mapGetters, mapActions } from "vuex";
import exportMixin from "./exportMixin";
import { WebPortalService } from "../../resource";

const PER_PAGE = 20;

export default {
  name: "logs-tables",
  mixins: [exportMixin],
  components: {
    RouteBreadCrumb,
    Modal,
    BasePagination,
    flatPicker,
    [Select.name]: Select,
    [Option.name]: Option,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn
  },
  data() {
    return {
      // relativeRange controls how start_date/end_date are filled. Non-custom
      // values recompute the window each time reload() runs, so "last 1 hour"
      // stays accurate across re-searches without the user re-picking.
      relativeRange: "last_1h",
      relativeOptions: [
        { value: "last_1h", label: "logs.range_last_1h" },
        { value: "last_2h", label: "logs.range_last_2h" },
        { value: "last_12h", label: "logs.range_last_12h" },
        { value: "last_24h", label: "logs.range_last_24h" },
        { value: "custom", label: "logs.range_custom" }
      ],
      start_date: "",
      end_date: "",
      session_id_filter: "",
      loading: false,
      showModal: false,
      activeSessionId: "",
      // showXmlModal opens a nested modal on top of the timeline. xmlPretty
      // is the formatted XML extracted from a MSwV4.Soap card's Detail field
      // (the bracketed payload between "[" and "]"). Stored separately so
      // copyXml can write it back to the clipboard verbatim.
      showXmlModal: false,
      xmlPretty: "",
      xmlFuncName: "",
      datepickerConfig: {
        allowInput: true,
        enableTime: true,
        enableSeconds: true,
        time_24hr: true,
        dateFormat: "Y-m-d H:i:S"
      },
      exportFn: () =>
        WebPortalService.EventLogExportV4({
          start_date: this.start_date,
          end_date: this.end_date,
          session_id: this.session_id_filter
        })
    };
  },
  computed: {
    ...mapGetters({
      sessions: "EventLog/getSessions",
      sessionEvents: "EventLog/getSessionEvents",
      total: "EventLog/getTotal",
      page: "EventLog/getPage",
      perPage: "EventLog/getPerPage",
      isDtacAdmin: "Auth/isDtacAdmin"
    }),
    rangeFrom() {
      return (this.page - 1) * this.perPage;
    },
    rangeTo() {
      const tail = this.rangeFrom + this.sessions.length;
      return tail > this.total ? this.total : tail;
    }
  },
  mounted() {
    // Seed the date pickers with sensible defaults but don't auto-fetch —
    // the admin has to click Search before any data is loaded. Pagination
    // then reuses the date range the admin committed to.
    this.applyRelativeRange();
  },
  methods: {
    ...mapActions({
      listSessions: "EventLog/listSessions",
      listSessionEvents: "EventLog/listSessionEvents"
    }),
    fmtDate(d) {
      return (
        d.getFullYear() +
        "-" +
        String(d.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(d.getDate()).padStart(2, "0") +
        " " +
        String(d.getHours()).padStart(2, "0") +
        ":" +
        String(d.getMinutes()).padStart(2, "0") +
        ":" +
        String(d.getSeconds()).padStart(2, "0")
      );
    },
    // applyRelativeRange writes start_date/end_date from the active dropdown
    // value. For "custom" it leaves whatever the user picked alone.
    applyRelativeRange() {
      if (this.relativeRange === "custom") return;
      const hours = {
        last_1h: 1,
        last_2h: 2,
        last_12h: 12,
        last_24h: 24
      }[this.relativeRange];
      if (!hours) return;
      const now = new Date();
      const back = new Date(now.getTime() - hours * 3600 * 1000);
      this.end_date = this.fmtDate(now);
      this.start_date = this.fmtDate(back);
    },
    // onRelativeChange just updates the date inputs (for visibility +
    // submission). It does NOT fetch — the admin still has to click Search.
    onRelativeChange() {
      this.applyRelativeRange();
    },
    // search is the explicit-fetch entry point (Search button) — recompute
    // the relative window then load page 1.
    search() {
      this.applyRelativeRange();
      this.reload(1);
    },
    // reload runs the actual fetch with whatever start_date / end_date are
    // already set. Pagination uses this directly without re-applying the
    // relative range — otherwise sliding "last 1 hour" shifts the window
    // on every click and total/page-count flips, dragging us back to page 1.
    async reload(page) {
      this.loading = true;
      try {
        await this.listSessions({
          start_date: this.start_date,
          end_date: this.end_date,
          session_id: this.session_id_filter,
          page: page,
          per_page: PER_PAGE
        });
      } finally {
        this.loading = false;
      }
    },
    onPageChange(p) {
      if (p === this.page) return;
      this.reload(p);
    },
    async openSession(row) {
      this.activeSessionId = row.SessionId;
      this.showModal = true;
      await this.listSessionEvents({ session_id: row.SessionId });
    },
    dotClass(level) {
      if (level === "ERROR") return "dot-error";
      if (level === "WARN") return "dot-warn";
      return "dot-info";
    },
    badgeClass(level) {
      if (level === "ERROR") return "badge-danger";
      if (level === "WARN") return "badge-warning";
      return "badge-info";
    },
    // isSoap flags events emitted by the MetaSwitch SOAP logger so the
    // "view XML" button only shows where there's actually XML to extract.
    isSoap(ev) {
      return ev && ev.FuncName === "MSwV4.Soap";
    },
    // writeClipboard copies text via the modern API and falls back to a
    // hidden textarea + execCommand for browsers/contexts that don't expose
    // navigator.clipboard (insecure origins, older Safari).
    writeClipboard(text) {
      if (
        typeof navigator !== "undefined" &&
        navigator.clipboard &&
        navigator.clipboard.writeText
      ) {
        return navigator.clipboard.writeText(text);
      }
      return new Promise((resolve, reject) => {
        try {
          const ta = document.createElement("textarea");
          ta.value = text;
          ta.style.position = "fixed";
          ta.style.opacity = "0";
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          ta.remove();
          resolve();
        } catch (e) {
          reject(e);
        }
      });
    },
    copyDetail(ev) {
      this.writeClipboard(ev.Detail || "").catch(() => {});
    },
    copyXml() {
      this.writeClipboard(this.xmlPretty || "").catch(() => {});
    },
    // extractBracketed pulls the payload between the first "[" and the
    // matching last "]". Logger format is "<funcName> <message>[<xml>]".
    // We use lastIndexOf("]") so XML containing brackets inside text nodes
    // still slices cleanly.
    extractBracketed(text) {
      if (!text) return "";
      const start = text.indexOf("[");
      const end = text.lastIndexOf("]");
      if (start < 0 || end <= start) return text;
      return text.substring(start + 1, end);
    },
    // formatXml indents a single-line XML string. No XML parser needed —
    // the SOAP envelopes we capture are well-formed and don't carry mixed
    // content, so a token-level reformat is enough.
    formatXml(xml) {
      if (!xml) return "";
      const PAD = "  ";
      // Drop XML declaration newline noise and normalise whitespace between tags.
      const compact = xml.replace(/>\s+</g, "><").trim();
      // Insert a newline after every "><" boundary so each tag sits on its own line.
      const lines = compact.replace(/></g, ">\n<").split("\n");
      let indent = 0;
      const out = [];
      for (const raw of lines) {
        const line = raw.trim();
        if (!line) continue;
        const isClose = /^<\//.test(line);
        const isSelfClose = /\/>$/.test(line);
        const isOpenClose = /^<[^!?][^>]*>[^<]*<\/[^>]+>$/.test(line); // <tag>x</tag>
        const isDecl = /^<\?/.test(line) || /^<!/.test(line);
        if (isClose) indent = Math.max(0, indent - 1);
        out.push(PAD.repeat(indent) + line);
        if (!isClose && !isSelfClose && !isOpenClose && !isDecl) indent++;
      }
      return out.join("\n");
    },
    openXml(ev) {
      const raw = this.extractBracketed(ev.Detail || "");
      this.xmlPretty = this.formatXml(raw);
      this.xmlFuncName = ev.FuncName || "";
      this.showXmlModal = true;
    }
  }
};
</script>

<style scoped>
.timeline-wrapper {
  position: relative;
  padding-left: 24px;
  max-height: 60vh;
  overflow-y: auto;
}
.timeline-wrapper::before {
  content: "";
  position: absolute;
  left: 8px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e9ecef;
}
.timeline-item {
  position: relative;
  margin-bottom: 18px;
}
.timeline-dot {
  position: absolute;
  left: -22px;
  top: 4px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 0 0 2px #e9ecef;
}
.dot-info {
  background: #11cdef;
}
.dot-warn {
  background: #fb6340;
}
.dot-error {
  background: #f5365c;
}
.timeline-content {
  background: #f6f9fc;
  border-radius: 6px;
  padding: 10px 14px;
  width: 90%;
}
.timeline-detail {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 8px;
  margin-top: 6px;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 240px;
  overflow-y: auto;
}
.export-error-detail {
  background: #fff5f7;
  border: 1px solid #f5365c;
  border-radius: 4px;
  padding: 10px;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 60vh;
  overflow-y: auto;
  margin: 0;
}
.xml-detail {
  background: #2d2d2d;
  color: #e8e8e8;
  border-radius: 4px;
  padding: 12px;
  font-size: 12px;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  white-space: pre;
  overflow: auto;
  max-height: 65vh;
  margin: 0;
}
</style>

<!-- Override Bootstrap's .modal-lg max-width so the timeline modal can hold
     long Detail payloads (request/response XML, error trees). Unscoped so it
     can target the Bootstrap modal-dialog element rendered outside the
     component's scoped CSS root. -->
<style>
.modal-lg {
  max-width: 90%;
}
</style>
